import { Typography } from '@mui/joy';
import { ArrowClockwise } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { CONSUMER_REFETCH_INTERVAL } from 'dashboard/hooks';
import { Color, Level, Size, Variant } from 'theme/enums';

interface IProps {
  dataUpdatedAt: number;
  isFetching: boolean;
  /** The query is failing, so no countdown is meaningful. */
  isStalled: boolean;
  onRefresh: () => void;
}

const SECOND = 1000;

// Standard visually-hidden style: the live region stays off-screen for sighted
// users (who read the visible countdown) while assistive tech still announces it.
const VISUALLY_HIDDEN_SX = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

/**
 * Makes the polling visible. Without it the list silently changes under the
 * reader and there is no way to tell a live page from a stale one.
 */
export const JobsRefreshStatus = ({ dataUpdatedAt, isFetching, isStalled, onRefresh }: IProps) => {
  const { t } = useTranslation();

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), SECOND);

    return () => clearInterval(timer);
  }, []);

  // Derived from when the data actually landed, not from a countdown of its own,
  // so an off-schedule refetch resets it correctly. Clamped at zero because
  // polling pauses in a background tab, which would otherwise run it negative.
  const secondsLeft = Math.max(
    0,
    Math.ceil((dataUpdatedAt + CONSUMER_REFETCH_INTERVAL - now) / SECOND)
  );

  // Announce only the two state transitions that matter — Refreshing and stalled —
  // and stay silent during the per-second countdown, so a screen reader isn't handed
  // a fresh announcement every tick while the visible label keeps ticking down.
  const getAnnouncement = () => {
    if (isFetching) {
      return t('messageConsumerJobsRefreshing');
    }

    if (isStalled) {
      return t('messageConsumerJobsRefreshStalled');
    }

    return '';
  };

  // The visible label is the announcement with a countdown fallback. Falling back only
  // when there is no announcement is also what keeps the label off "Refreshing in 0s"
  // while the query is failing, since dataUpdatedAt never advances then — a countdown
  // that would quietly lie.
  const getLabel = () =>
    getAnnouncement() || t('messageConsumerJobsRefreshingIn', { seconds: secondsLeft });

  return (
    <>
      <Typography level={Level.BODY_XS} sx={{ color: 'text.tertiary' }}>
        {getLabel()}
      </Typography>

      <Typography aria-live="polite" level={Level.BODY_XS} sx={VISUALLY_HIDDEN_SX}>
        {getAnnouncement()}
      </Typography>

      <Button
        color={Color.NEUTRAL}
        icon={<ArrowClockwise />}
        loading={isFetching}
        onClick={onRefresh}
        size={Size.SM}
        tooltipProps={{ children: null, title: t('tooltipRefreshConsumerJobs') }}
        variant={Variant.PLAIN}
      />
    </>
  );
};
